import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TrashIcon } from 'assets/icons/tresh.svg';
import clsx from 'clsx';
import IconButton from 'components/IconButton/IconButton';
import Table from 'components/Table/Table';
import TableBody from 'components/Table/TableBody';
import TableCell from 'components/Table/TableCell';
import TableHead from 'components/Table/TableHead';
import TableRow from 'components/Table/TableRow';
import TableSort from 'components/Table/TableSort';
import { SORT_BY_OPTIONS_EXCLUDE } from 'constants/general';

import type ISelectSupplierTable from './interfaces/IItemsTable';

import styles from './scss/ItemsTable.module.scss';

function ItemsTable({
  items,
  headCells,
  onLoadMore,
  hasMore,
  isLoadingMore,
  isInfoTable,
  naceCodesData,
  onDeleteItem,
  onSortChange,
  orderBy,
  locationsCountries,
}: ISelectSupplierTable) {
  const { t } = useTranslation();
  const hyphen = t('common.hyphen');

  const scrollRef = useRef<HTMLDivElement>(null);
  const throttleTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    const handleScroll = () => {
      if (throttleTimeout.current) return;
      throttleTimeout.current = setTimeout(() => {
        if (!el || isLoadingMore || !hasMore) {
          throttleTimeout.current = null;
          return;
        }
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 50) {
          onLoadMore();
        }
        throttleTimeout.current = null;
      }, 150);
    };
    if (el) {
      el.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (el) {
        el.removeEventListener('scroll', handleScroll);
      }
      if (throttleTimeout.current) {
        clearTimeout(throttleTimeout.current);
        throttleTimeout.current = null;
      }
    };
  }, [isLoadingMore, hasMore, onLoadMore]);

  const locationCountry = (countryCode: string) => {
    const country = locationsCountries?.find((countryItem) => countryItem.iso2 === countryCode);
    return country?.name || countryCode;
  };

  return (
    <div className={styles.root}>
      <div
        className={styles.tableBodyScroll}
        ref={scrollRef}
        aria-label="Supplier table body"
        role="region"
      >
        <Table
          className={clsx(styles.table, {
            [styles.tableInfoTable]: isInfoTable,
          })}
        >
          <TableHead className={clsx(styles.header, {
            [styles.headerInfoTable]: isInfoTable,
          })}
          >
            {headCells.map(({
              id, label,
            }, index) => (
              <TableCell head className={styles.tableCell} key={id}>
                <div>
                  <span className={clsx(styles.tableCellLabel, {
                    [styles.tableCellLabelFirst]: index === 2,
                    [styles.tableCellLabelSecond]: index === 1,
                  })}
                  >
                    {id !== SORT_BY_OPTIONS_EXCLUDE.ACTION
                    && id !== SORT_BY_OPTIONS_EXCLUDE.INDUSTRY_CODE ? (
                      <TableSort
                        active={orderBy.includes(id) || orderBy.includes(`-${id}`)}
                        onClickSort={() => onSortChange(id)}
                      >
                        <span>{t(label)}</span>
                      </TableSort>
                      ) : (<span>{t(label)}</span>)}
                  </span>
                </div>
              </TableCell>
            ))}
          </TableHead>
          <TableBody className={styles.body}>
            {items ? items?.map((item, index) => (
              <TableRow key={[item.id, index].join('_')}>
                <TableCell>
                  <div className={styles.staticCell}>
                    <span>{locationCountry(item.country)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  {naceCodesData.find((nace) => item.naceSection?.includes(nace.value))?.label || hyphen}
                </TableCell>
                <TableCell>
                  {naceCodesData.find((nace) => item.naceSection?.includes(nace.value))?.divisions
                    ?.filter((division) => item.naceCodes?.includes(division.value))
                    ?.map((division, divisionIndex, filteredDivisions) => (
                      <span key={division.label}>
                        {division.label}
                        {divisionIndex < filteredDivisions.length - 1 && ', '}
                      </span>
                    )) || hyphen}
                </TableCell>
                <TableCell>
                  {!item.isGlobal ? (
                    <IconButton
                      icon={<TrashIcon />}
                      onClick={() => onDeleteItem?.(item.id || -1)}
                    />
                  ) : null}
                </TableCell>
              </TableRow>
            )) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export default ItemsTable;
